import { useEffect, useState, useCallback } from 'react';
import { Save, Eye, EyeOff, AlertTriangle, CheckCircle, Wifi, Link2 } from 'lucide-react';
import { api } from '../../api/client';

type Tenant = { id: string; name: string };

type Settings = {
  apiKey: string;
  eppApiKey: string;
  hasApiKey?: boolean;
  hasEppApiKey?: boolean;
  environment: 'demo' | 'production';
  companyName: string;
  taxId: string;
  registrationNumber: string;
  bankAccount: string;
  address: string;
  city: string;
  postalCode: string;
  email: string;
  callbackUrlSales?: string;
  callbackUrlPurchase?: string;
};

const emptySettings: Settings = {
  apiKey: '',
  eppApiKey: '',
  hasApiKey: false,
  hasEppApiKey: false,
  environment: 'demo',
  companyName: '',
  taxId: '',
  registrationNumber: '',
  bankAccount: '',
  address: '',
  city: '',
  postalCode: '',
  email: '',
};

export function EFakturaSettingsPanel() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [form, setForm] = useState<Settings>(emptySettings);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showEppKey, setShowEppKey] = useState(false);
  const [publicBaseUrl, setPublicBaseUrl] = useState('');
  const [registering, setRegistering] = useState<string | null>(null);

  useEffect(() => {
    api.get<Tenant[]>('/admin/tenants').then((rows) => {
      setTenants(rows);
      if (rows.length > 0) setSelectedTenantId(rows[0].id);
    }).catch(() => {});
  }, []);

  const fetchSettings = useCallback(async () => {
    if (!selectedTenantId) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const data = await api.get<Settings>(`/admin/efaktura/settings/${selectedTenantId}`);
      setForm({ ...emptySettings, ...data });
    } catch (e: any) {
      if (e?.status === 404) {
        setForm(emptySettings);
      } else {
        setError(e?.data?.message || e?.message || 'Greška pri učitavanju podešavanja');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedTenantId]);

  useEffect(() => {
    if (selectedTenantId) fetchSettings();
  }, [selectedTenantId, fetchSettings]);

  const set = (key: keyof Settings, value: string) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTenantId) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await api.put(`/admin/efaktura/settings/${selectedTenantId}`, form);
      setSuccess('Podešavanja sačuvana uspešno.');
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška pri čuvanju');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!selectedTenantId) return;
    setTesting(true);
    setError(null);
    setSuccess(null);
    try {
      await api.post(`/admin/efaktura/settings/${selectedTenantId}/test-connection`);
      setSuccess('Konekcija uspešna! SEF API je dostupan.');
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Konekcija neuspešna');
    } finally {
      setTesting(false);
    }
  };

  const handleRegisterCallback = async (kind: 'sales' | 'purchase' | 'both') => {
    if (!selectedTenantId) return;
    if (!publicBaseUrl.trim() || !/^https:\/\//i.test(publicBaseUrl)) {
      setError('Potreban je HTTPS public URL (npr. https://api.mojaapp.com)');
      return;
    }
    setRegistering(kind);
    setError(null);
    setSuccess(null);
    try {
      const res = await api.post<{ sales?: string; purchase?: string }>(
        `/admin/efaktura/settings/${selectedTenantId}/register-callback`,
        { publicBaseUrl: publicBaseUrl.trim(), kind },
      );
      setSuccess(`Callback registrovan. ${res.sales ? `Sales: ${res.sales}` : ''} ${res.purchase ? `Purchase: ${res.purchase}` : ''}`.trim());
      await fetchSettings();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška pri registraciji');
    } finally {
      setRegistering(null);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <label style={{ fontSize: 12, color: 'var(--color-text-muted)', fontWeight: 500 }}>Tenant:</label>
        <select
          value={selectedTenantId}
          onChange={(e) => setSelectedTenantId(e.target.value)}
          style={selectStyle}
        >
          {tenants.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </div>

      {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
      {success && <div style={successBox}><CheckCircle size={14} /> {success}</div>}

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-text-muted)' }}>Učitavanje...</div>
      ) : (
        <form onSubmit={handleSave}>
          <Section title="API podešavanja">
            <Field label={`API Key${form.hasApiKey ? ' (konfigurisan)' : ''}`}>
              <div style={{ position: 'relative' }}>
                <input
                  type={showApiKey ? 'text' : 'password'}
                  value={form.apiKey}
                  onChange={(e) => set('apiKey', e.target.value)}
                  style={inputStyle}
                  placeholder={form.hasApiKey ? '•••••••••• (ostavi prazno da ne menjaš)' : 'SEF API ključ'}
                />
                <button type="button" onClick={() => setShowApiKey(!showApiKey)} style={toggleBtn}>
                  {showApiKey ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </Field>
            <Field label={`EPP API Key${form.hasEppApiKey ? ' (konfigurisan)' : ''}`}>
              <div style={{ position: 'relative' }}>
                <input
                  type={showEppKey ? 'text' : 'password'}
                  value={form.eppApiKey}
                  onChange={(e) => set('eppApiKey', e.target.value)}
                  style={inputStyle}
                  placeholder={form.hasEppApiKey ? '•••••••••• (ostavi prazno da ne menjaš)' : 'EPP API ključ'}
                />
                <button type="button" onClick={() => setShowEppKey(!showEppKey)} style={toggleBtn}>
                  {showEppKey ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </Field>
            <Field label="Okruženje">
              <select value={form.environment} onChange={(e) => set('environment', e.target.value)} style={selectStyle}>
                <option value="demo">Demo</option>
                <option value="production">Production</option>
              </select>
            </Field>
          </Section>

          <Section title="Podaci o firmi">
            <Field label="Naziv firme">
              <input type="text" value={form.companyName} onChange={(e) => set('companyName', e.target.value)} style={inputStyle} />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Field label="PIB">
                <input type="text" value={form.taxId} onChange={(e) => set('taxId', e.target.value)} style={inputStyle} />
              </Field>
              <Field label="Matični broj">
                <input type="text" value={form.registrationNumber} onChange={(e) => set('registrationNumber', e.target.value)} style={inputStyle} />
              </Field>
            </div>
            <Field label="Tekući račun">
              <input type="text" value={form.bankAccount} onChange={(e) => set('bankAccount', e.target.value)} style={inputStyle} placeholder="265-1234567890-12" />
            </Field>
            <Field label="Adresa">
              <input type="text" value={form.address} onChange={(e) => set('address', e.target.value)} style={inputStyle} />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14 }}>
              <Field label="Grad">
                <input type="text" value={form.city} onChange={(e) => set('city', e.target.value)} style={inputStyle} />
              </Field>
              <Field label="Poštanski broj">
                <input type="text" value={form.postalCode} onChange={(e) => set('postalCode', e.target.value)} style={inputStyle} />
              </Field>
            </div>
            <Field label="Email">
              <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} style={inputStyle} />
            </Field>
          </Section>

          <Section title="Callback (webhook)">
            <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 8 }}>
              SEF će slati obaveštenja o promeni statusa na registrovan URL. Potreban je javno dostupan HTTPS endpoint.
            </div>
            <Field label="Public base URL">
              <input
                type="text"
                value={publicBaseUrl}
                onChange={(e) => setPublicBaseUrl(e.target.value)}
                style={inputStyle}
                placeholder="https://api.mojaapp.com"
              />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <CallbackStatus label="Izlazne (sales)" url={form.callbackUrlSales} />
              <CallbackStatus label="Ulazne (purchase)" url={form.callbackUrlPurchase} />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <button type="button" onClick={() => handleRegisterCallback('sales')} disabled={registering !== null} style={btnSecondary}>
                <Link2 size={14} /> {registering === 'sales' ? 'Registrujem...' : 'Registruj sales'}
              </button>
              <button type="button" onClick={() => handleRegisterCallback('purchase')} disabled={registering !== null} style={btnSecondary}>
                <Link2 size={14} /> {registering === 'purchase' ? 'Registrujem...' : 'Registruj purchase'}
              </button>
              <button type="button" onClick={() => handleRegisterCallback('both')} disabled={registering !== null} style={btnSecondary}>
                <Link2 size={14} /> {registering === 'both' ? 'Registrujem...' : 'Registruj oba'}
              </button>
            </div>
          </Section>

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button type="button" onClick={handleTestConnection} disabled={testing} style={{ ...btnSecondary, opacity: testing ? 0.5 : 1 }}>
              <Wifi size={14} /> {testing ? 'Testiranje...' : 'Testiraj konekciju'}
            </button>
            <div style={{ flex: 1 }} />
            <button type="submit" disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.5 : 1 }}>
              <Save size={14} /> {saving ? 'Čuvanje...' : 'Sačuvaj'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 14, background: 'var(--color-bg)', borderRadius: 8, border: '1px solid var(--color-border)', marginBottom: 16 }}>
      <h4 style={{ margin: 0, fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{title}</h4>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4, fontWeight: 500 }}>{label}</label>
      {children}
    </div>
  );
}

function CallbackStatus({ label, url }: { label: string; url: string | undefined | null }) {
  return (
    <div style={{ padding: 10, borderRadius: 6, background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
      <div style={{ fontSize: 11, color: 'var(--color-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{label}</div>
      {url ? (
        <div style={{ fontSize: 11, color: 'var(--color-text)', wordBreak: 'break-all', fontFamily: 'Menlo, Consolas, monospace' }}>{url}</div>
      ) : (
        <div style={{ fontSize: 12, color: 'var(--color-text-muted)', fontStyle: 'italic' }}>Nije registrovan</div>
      )}
    </div>
  );
}

/* ─── Style constants ─── */
const inputStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, width: '100%', background: 'var(--color-bg-surface)', color: 'var(--color-text)' };
const selectStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, background: 'var(--color-bg-surface)', color: 'var(--color-text)', minWidth: 200 };
const btnPrimary: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 6, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnSecondary: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: 'var(--color-bg-surface)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500 };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 16 };
const successBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(34,197,94,0.15)', color: '#4ade80', fontSize: 13, marginBottom: 16 };
const toggleBtn: React.CSSProperties = { position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', padding: 4 };
